import React, { useState, useEffect } from 'react';
import MarkdownContent from './MarkdownContent';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  Grid,
  Paper,
  CircularProgress,
  Alert,
  Divider,
  IconButton,
  Tooltip,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  TextField,
  Checkbox,
  FormControlLabel,
  Stack,
  MenuItem,
} from '@mui/material';
import {
  Close as CloseIcon,
  CheckCircle as ResolvedIcon,
  Error as ActiveIcon,
  Update as UpdateIcon,
  Notifications as NotificationIcon,
  NotificationsOff as NoNotificationIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import apiService from '../services/api';

import OutageCustomerNotifications from './OutageCustomerNotifications';

// Matches the section-header pattern used across the app (ServiceDetailView,
// SupaBuildingAddress, NbnOutageFormDialog, etc.) — navy band bleeding to the
// edges of a p:2.5 Paper.
const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box sx={{ bgcolor: '#1e3a5c', px: 2.5, py: 0.75, mx: -2.5, mt: -2.5, mb: 2.5, borderRadius: '4px 4px 0 0' }}>
    <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase', color: '#ffffff' }}>
      {children}
    </Typography>
  </Box>
);

const UPDATE_STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'planned', label: 'Planned' },
  { value: 'resolved', label: 'Resolved' },
];

interface OutageDetailsModalProps {
  open: boolean;
  onClose: () => void;
  incidentId: string;
}

interface TimelineEvent {
  timestamp: string;
  event_type: string;
  description: string;
  status: string;
  source: string;
}

interface LinkedCustomer {
  id: string;
  service_id: string;
  member_id: string;
  organisation_name: string;
  contact_name: string;
  email: string;
  mobile_number: string;
  service_address: string;
  notification_preferences: any;
  link_type: string;
  link_value: string;
}

interface ImpactedCustomer {
  id: string;
  service_id: string;
  member_id: string;
  organisation_name: string;
  contact_name: string;
  email: string;
  mobile_number: string;
  service_address: string;
  notification_preferences: any;
  affected: boolean;
  notification_sent: boolean;
  sms_notification_sent: boolean;
  linked_at: string;
}

interface OutageData {
  outage: any;
  timeline: TimelineEvent[];
  linked_customers: LinkedCustomer[];
  notification_ready: boolean;
}

interface ImpactedCustomersData {
  incident_id: string;
  total_impacted: number;
  customers: ImpactedCustomer[];
}

const OutageDetailsModal: React.FC<OutageDetailsModalProps> = ({
  open,
  onClose,
  incidentId,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState<OutageData | null>(null);
  const [impactedCustomers, setImpactedCustomers] = useState<ImpactedCustomersData | null>(null);
  const [customersLoading, setCustomersLoading] = useState(false);

  const [updateText, setUpdateText] = useState('');
  const [updateStatus, setUpdateStatus] = useState('');
  const [updateNotifyEmail, setUpdateNotifyEmail] = useState(true); // customers are emailed each update unless staff untick this
  const [updateNotifyResult, setUpdateNotifyResult] = useState<{ severity: 'success' | 'info' | 'warning'; text: string } | null>(null);
  const [notificationsRefresh, setNotificationsRefresh] = useState(0);
  const [updateNotifySms, setUpdateNotifySms] = useState(false);
  const [updateSubmitting, setUpdateSubmitting] = useState(false);
  const [updateError, setUpdateError] = useState('');

  const [notifyEmailSending, setNotifyEmailSending] = useState(false);
  const [notifySmsSending, setNotifySmsSending] = useState(false);
  const [notifyResult, setNotifyResult] = useState<{ channel: string; sent: number; failed: number; skipped: number; already?: number } | null>(null);
  const [notifyError, setNotifyError] = useState('');
  const [bellSendingId, setBellSendingId] = useState<string | null>(null);
  const [bellResult, setBellResult] = useState<{ severity: 'success' | 'info' | 'warning' | 'error'; text: string } | null>(null);

  useEffect(() => {
    if (open && incidentId && incidentId.trim() !== "" && incidentId !== "null" && incidentId !== "undefined") {
      fetchOutageDetails();
      fetchImpactedCustomers();
    } else if (open && (!incidentId || incidentId.trim() === "" || incidentId === "null" || incidentId === "undefined")) {
      setError("No valid incident ID provided for this outage");
      setLoading(false);
    }
  }, [open, incidentId]);

  const fetchOutageDetails = async () => {
    try {
      setLoading(true);
      if (!incidentId || incidentId.trim() === "" || incidentId === "null" || incidentId === "undefined") {
        throw new Error("Invalid or missing incident ID");
      }
      setError('');
      const result = await apiService.getOutageTimeline(incidentId);
      setData(result);
    } catch (err: any) {
      setError('Failed to load outage details');
      console.error('Error fetching outage details:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchImpactedCustomers = async () => {
    try {
      setCustomersLoading(true);
      if (!incidentId || incidentId.trim() === "" || incidentId === "null" || incidentId === "undefined") {
        return;
      }
      const result = await apiService.getImpactedCustomers(incidentId);
      setImpactedCustomers(result);
    } catch (err: any) {
      console.error('Error fetching impacted customers:', err);
      // Don't set error state for customers as it's supplementary info
    } finally {
      setCustomersLoading(false);
    }
  };

  const handleAddUpdate = async () => {
    if (!data?.outage?.id || !updateText.trim()) return;
    try {
      setUpdateSubmitting(true);
      setUpdateError('');
      const res = await apiService.addOutageUpdate(data.outage.id, {
        description: updateText.trim(),
        status: updateStatus || undefined,
        notify_email: updateNotifyEmail,
        notify_sms: updateNotifySms,
      });
      const email = res?.notifications?.email;
      if (updateNotifyEmail && email) {
        if (email.muted) setUpdateNotifyResult({ severity: 'info', text: 'Update added. Automatic notifications are switched off for this outage, so customers were not emailed.' });
        else if (email.mode === 'off') setUpdateNotifyResult({ severity: 'info', text: 'Update added. Automatic customer notifications are off in Settings, so customers were not emailed.' });
        else if (email.updates_off) setUpdateNotifyResult({ severity: 'info', text: 'Update added. Update emails are switched off in Settings, so customers were not emailed.' });
        else if (email.held) setUpdateNotifyResult({ severity: 'warning', text: 'Update added, but this outage affects more accounts than the automatic limit, so it was held. Use Send Email to send it manually.' });
        else if (email.mode === 'test') setUpdateNotifyResult({ severity: 'info', text: `Update added. Test mode: nothing was sent to customers; ${email.test} account(s) would have been emailed (the test recipients have the details).` });
        else setUpdateNotifyResult({ severity: 'success', text: `Update added. ${email.sent} account(s) emailed${email.skipped ? `, ${email.skipped} skipped (no email address or notifications off)` : ''}${email.failed ? `, ${email.failed} failed` : ''}.` });
      } else {
        setUpdateNotifyResult(null);
      }
      setNotificationsRefresh((n) => n + 1);
      setUpdateText('');
      setUpdateStatus('');
      setUpdateNotifyEmail(true);
      setUpdateNotifySms(false);
      await Promise.all([fetchOutageDetails(), fetchImpactedCustomers()]);
    } catch (err: any) {
      setUpdateError(err.response?.data?.detail || err.message || 'Failed to add update');
    } finally {
      setUpdateSubmitting(false);
    }
  };

  /** The bell next to a customer: send this outage's notice to that customer now (and again, if already sent). */
  const handleBellClick = async (customer: ImpactedCustomer) => {
    if (!data?.outage?.id || bellSendingId) return;
    const name = customer.organisation_name || customer.contact_name || 'this customer';
    const noticeName = data.outage.status === 'resolved' ? 'resolved' : data.outage.planned ? 'planned maintenance' : 'outage';
    const question = customer.notification_sent
      ? `${name} has already been notified. Send the ${noticeName} notice again?`
      : `Send the ${noticeName} notice to ${name} now?\n\nIt goes from support@sonicnet.net.au to the account email and any Technical contacts, whatever the automatic notification settings are.`;
    if (!window.confirm(question)) return;
    try {
      setBellSendingId(customer.id);
      setBellResult(null);
      const res = await apiService.sendOutageNotificationNow(data.outage.id, { account_ids: [customer.id], resend: !!customer.notification_sent });
      const r = res.results[0];
      if (r?.status === 'sent') setBellResult({ severity: 'success', text: `Notice sent to ${name} (${r.to}).` });
      else if (r?.status === 'skipped') setBellResult({ severity: 'warning', text: `${name} wasn't emailed: ${r.reason || 'no email address, or email notifications are turned off for this account'}.` });
      else if (r?.status === 'failed') setBellResult({ severity: 'error', text: `Sending to ${name} failed${r.reason ? `: ${r.reason}` : ''}.` });
      else setBellResult({ severity: 'info', text: `${name} has already been notified.` });
      setNotificationsRefresh((n) => n + 1);
      await fetchImpactedCustomers();
    } catch (err: any) {
      setBellResult({ severity: 'error', text: err.response?.data?.detail || err.message || 'Failed to send the notification' });
    } finally {
      setBellSendingId(null);
    }
  };

  const handleNotify = async (channel: 'email' | 'sms') => {
    if (!data?.outage?.id) return;
    const total = impactedCustomers?.total_impacted || 0;
    const alreadySent = (impactedCustomers?.customers || []).some((c) =>
      channel === 'email' ? c.notification_sent : c.sms_notification_sent
    );
    const verb = alreadySent ? 'Resend' : 'Send';
    if (!window.confirm(`${verb} ${channel.toUpperCase()} notifications to ${total} impacted customer(s)?`)) return;
    const setSending = channel === 'email' ? setNotifyEmailSending : setNotifySmsSending;
    try {
      setSending(true);
      setNotifyError('');
      setNotifyResult(null);
      const result = channel === 'email'
        ? await apiService.notifyOutageEmail(data.outage.id, alreadySent)
        : await apiService.notifyOutageSms(data.outage.id);
      setNotifyResult({ channel, ...result });
      await fetchImpactedCustomers();
    } catch (err: any) {
      setNotifyError(err.response?.data?.detail || err.message || `Failed to send ${channel} notifications`);
    } finally {
      setSending(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'resolved':
        return <ResolvedIcon color="success" />;
      case 'active':
        return <ActiveIcon color="error" />;
      default:
        return <UpdateIcon color="primary" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'resolved':
        return 'success';
      case 'active':
        return 'error';
      case 'planned':
        return 'warning';
      default:
        return 'default';
    }
  };

  const formatDateTime = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    try {
      return format(new Date(dateString), 'PPpp');
    } catch {
      return dateString;
    }
  };

  const formatDuration = (start: string | null, end: string | null) => {
    if (!start) return 'N/A';
    if (!end) return 'Ongoing';
    
    try {
      const startDate = new Date(start);
      const endDate = new Date(end);
      const diffMs = endDate.getTime() - startDate.getTime();
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
      
      if (diffHours > 0) {
        return `${diffHours}h ${diffMinutes}m`;
      } else {
        return `${diffMinutes}m`;
      }
    } catch {
      return 'N/A';
    }
  };

  if (loading) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogContent>
          <Box display="flex" justifyContent="center" alignItems="center" py={4}>
            <CircularProgress />
            <Typography variant="body2" sx={{ ml: 2 }}>
              Loading outage details...
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  if (error) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  }

  if (!data) return null;

  const { outage, timeline, linked_customers } = data;
  const emailAlreadySent = (impactedCustomers?.customers || []).some((c) => c.notification_sent);
  const smsAlreadySent = (impactedCustomers?.customers || []).some((c) => c.sms_notification_sent);
  const totalImpacted = impactedCustomers?.total_impacted || 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h5" component="div">
            {outage.title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Incident: {outage.incident_id}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip
            label={outage.status.toUpperCase()}
            color={getStatusColor(outage.status) as any}
            size="small"
          />
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Grid container spacing={3}>
          {/* Main Outage Information - Left Column */}
          <Grid item xs={12} md={8}>
            <Paper variant="outlined" sx={{ p: 2.5 }}>
              <SectionLabel>Outage Details</SectionLabel>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">Status</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                      {getStatusIcon(outage.status)}
                      <Typography variant="body1" sx={{ ml: 1 }}>
                        {outage.status.charAt(0).toUpperCase() + outage.status.slice(1)}
                      </Typography>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">Severity</Typography>
                    <Typography variant="body1">{outage.severity || 'Medium'}</Typography>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">Start Time</Typography>
                    <Typography variant="body1">{formatDateTime(outage.start_time)}</Typography>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">Resolved At</Typography>
                    <Typography variant="body1">{formatDateTime(outage.resolved_at)}</Typography>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">Duration</Typography>
                    <Typography variant="body1">
                      {formatDuration(outage.start_time, outage.resolved_at)}
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">Source</Typography>
                    <Typography variant="body1">{outage.source_system || 'Vocus'}</Typography>
                  </Grid>
                </Grid>
                
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="text.secondary">Description</Typography>
                  <Box sx={{ mt: 0.5 }}>
                    <MarkdownContent>{outage.description || ''}</MarkdownContent>
                  </Box>
                </Box>
            </Paper>
          </Grid>

          {/* Right Column - Location & Impact + Impacted Customers */}
          <Grid item xs={12} md={4}>
            {/* Location Information */}
            {outage.location_area && (
              <Paper variant="outlined" sx={{ p: 2.5, mb: 2 }}>
                <SectionLabel>Location & Impact</SectionLabel>
                <Typography variant="body2" color="text.secondary">Affected Area</Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  {outage.location_area}
                </Typography>

                {outage.impact_description && (
                  <>
                    <Typography variant="body2" color="text.secondary">Impact Description</Typography>
                    <MarkdownContent>{outage.impact_description}</MarkdownContent>
                  </>
                )}
              </Paper>
            )}

            {/* Impacted Customers */}
            <Paper variant="outlined" sx={{ p: 2.5, mb: 2 }}>
              <Box sx={{ bgcolor: '#1e3a5c', px: 2.5, py: 0.75, mx: -2.5, mt: -2.5, mb: 2.5, borderRadius: '4px 4px 0 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase', color: '#ffffff' }}>
                  Impacted Customers
                </Typography>
                {customersLoading && <CircularProgress size={14} sx={{ color: '#ffffff' }} />}
              </Box>

                {impactedCustomers && impactedCustomers.customers.length > 0 ? (
                  <>
                    <Box sx={{ mb: 2 }}>
                      <Chip 
                        label={`Total: ${impactedCustomers.total_impacted}`}
                        color="primary"
                        size="small"
                      />
                    </Box>
                    {bellResult && (
                      <Alert severity={bellResult.severity} sx={{ mb: 2 }} onClose={() => setBellResult(null)}>{bellResult.text}</Alert>
                    )}

                    {impactedCustomers.customers.map((customer, index) => (
                      <Box key={customer.id} sx={{ mb: index < impactedCustomers.customers.length - 1 ? 2 : 0 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="subtitle2" color="primary">
                              {customer.organisation_name || customer.contact_name || 'Unknown Customer'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Service ID: {customer.service_id}
                            </Typography>
                            {customer.member_id && (
                              <Typography variant="body2" color="text.secondary">
                                Member ID: {customer.member_id}
                              </Typography>
                            )}
                            {customer.email && (
                              <Typography variant="body2" color="text.secondary">
                                Email: {customer.email}
                              </Typography>
                            )}
                            {customer.service_address && (
                              <Typography variant="body2" color="text.secondary">
                                Address: {customer.service_address}
                              </Typography>
                            )}
                          </Box>
                          
                          <Box sx={{ ml: 1 }}>
                            <Tooltip title={customer.notification_sent ? 'Notified. Click to send the notice again' : 'Not notified. Click to send the notice now'}>
                              <span>
                                <IconButton size="small" disabled={bellSendingId === customer.id} onClick={() => handleBellClick(customer)} aria-label="Send notification">
                                  {bellSendingId === customer.id ? (
                                    <CircularProgress size={18} />
                                  ) : customer.notification_sent ? (
                                    <NotificationIcon color="success" fontSize="small" />
                                  ) : (
                                    <NoNotificationIcon color="action" fontSize="small" />
                                  )}
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Box>
                        </Box>

                        {/* Status chips */}
                        <Box sx={{ mt: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          <Chip
                            size="small"
                            label={customer.affected ? "Affected" : "Not Affected"}
                            color={customer.affected ? "error" : "default"}
                            variant="outlined"
                          />
                          <Chip
                            size="small"
                            label={customer.notification_sent ? "Notified" : "Not Notified"}
                            color={customer.notification_sent ? "success" : "default"}
                            variant="outlined"
                          />
                        </Box>

                        {customer.linked_at && (
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                            Linked: {formatDateTime(customer.linked_at)}
                          </Typography>
                        )}
                        
                        {index < impactedCustomers.customers.length - 1 && <Divider sx={{ mt: 2 }} />}
                      </Box>
                    ))}
                  </>
                ) : (
                  // Fallback to linked customers from timeline if no impacted customers data
                  linked_customers && linked_customers.length > 0 ? (
                    <>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        ({linked_customers.length} from timeline)
                      </Typography>
                      {linked_customers.map((customer, index) => (
                        <Box key={customer.id} sx={{ mb: index < linked_customers.length - 1 ? 2 : 0 }}>
                          <Typography variant="subtitle2" color="primary">
                            {customer.organisation_name || customer.contact_name || 'Unknown Customer'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Service ID: {customer.service_id}
                          </Typography>
                          {customer.member_id && (
                            <Typography variant="body2" color="text.secondary">
                              Member ID: {customer.member_id}
                            </Typography>
                          )}
                          {customer.email && (
                            <Typography variant="body2" color="text.secondary">
                              Email: {customer.email}
                            </Typography>
                          )}
                          {customer.service_address && (
                            <Typography variant="body2" color="text.secondary">
                              Address: {customer.service_address}
                            </Typography>
                          )}
                          
                          {/* Notification Status */}
                          <Box sx={{ mt: 1 }}>
                            <Chip
                              size="small"
                              label={data.notification_ready ? "Notification Ready" : "No Notifications"}
                              color={data.notification_ready ? "success" : "default"}
                              variant="outlined"
                            />
                          </Box>
                          
                          {index < linked_customers.length - 1 && <Divider sx={{ mt: 2 }} />}
                        </Box>
                      ))}
                    </>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      {customersLoading ? "Loading customers..." : "No impacted customers found"}
                    </Typography>
                  )
                )}
            </Paper>
          </Grid>

          {/* Updates & Notifications */}
          <Grid item xs={12}>
            <Paper variant="outlined" sx={{ p: 2.5 }}>
              <SectionLabel>Updates & Notifications</SectionLabel>

                <OutageCustomerNotifications outageId={outage.id} refreshKey={notificationsRefresh} />
                <Divider sx={{ mb: 2 }} />

                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  Manual send (immediate, to all impacted customers, regardless of the automatic settings):
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={notifyEmailSending || totalImpacted === 0}
                    onClick={() => handleNotify('email')}
                  >
                    {notifyEmailSending ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
                    {emailAlreadySent ? 'Resend Email' : 'Send Email'}
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={notifySmsSending || totalImpacted === 0}
                    onClick={() => handleNotify('sms')}
                  >
                    {notifySmsSending ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
                    {smsAlreadySent ? 'Resend SMS' : 'Send SMS'}
                  </Button>
                  {totalImpacted === 0 && (
                    <Typography variant="caption" color="text.secondary">
                      No impacted customers linked yet — add impacted buildings/services to this outage first.
                    </Typography>
                  )}
                </Box>

                {notifyError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setNotifyError('')}>{notifyError}</Alert>}
                {notifyResult && (
                  <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotifyResult(null)}>
                    {notifyResult.channel.toUpperCase()}: {notifyResult.sent} sent, {notifyResult.failed} failed, {notifyResult.skipped} skipped (no contact detail){notifyResult.already ? `, ${notifyResult.already} already notified` : ''}
                  </Alert>
                )}

                <Divider sx={{ mb: 2 }} />

                <Stack spacing={1} sx={{ mb: 1 }}>
                  <TextField
                    label="Add update"
                    multiline
                    minRows={2}
                    fullWidth
                    size="small"
                    value={updateText}
                    onChange={(e) => setUpdateText(e.target.value)}
                  />
                  <TextField
                    select
                    label="Status"
                    size="small"
                    sx={{ maxWidth: 220 }}
                    value={updateStatus || outage.status || 'active'}
                    onChange={(e) => setUpdateStatus(e.target.value)}
                    helperText="Change alongside this update, e.g. to Resolved"
                  >
                    {UPDATE_STATUS_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                  {updateError && <Alert severity="error" onClose={() => setUpdateError('')}>{updateError}</Alert>}
                  {updateNotifyResult && <Alert severity={updateNotifyResult.severity} onClose={() => setUpdateNotifyResult(null)}>{updateNotifyResult.text}</Alert>}
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                    <Box>
                      <FormControlLabel
                        control={<Checkbox size="small" checked={updateNotifyEmail} onChange={(e) => setUpdateNotifyEmail(e.target.checked)} />}
                        label="Email customers this update (account email + Technical contacts)"
                      />
                      <FormControlLabel
                        control={<Checkbox size="small" checked={updateNotifySms} onChange={(e) => setUpdateNotifySms(e.target.checked)} />}
                        label="SMS customers this update"
                      />
                    </Box>
                    <Button
                      variant="contained"
                      size="small"
                      disabled={updateSubmitting || !updateText.trim()}
                      onClick={handleAddUpdate}
                    >
                      {updateSubmitting ? 'Adding…' : 'Add Update'}
                    </Button>
                  </Box>
                </Stack>
            </Paper>
          </Grid>

          {/* Timeline - Full Width Bottom */}
          <Grid item xs={12}>
            <Paper variant="outlined" sx={{ p: 2.5 }}>
              <SectionLabel>Timeline</SectionLabel>

                <List dense>
                  {timeline.map((event, index) => (
                    <ListItem key={index} alignItems="flex-start">
                      <ListItemAvatar>
                        <Avatar 
                          sx={{ 
                            bgcolor: event.event_type === 'resolved' ? 'success.main' : 'primary.main',
                            width: 32,
                            height: 32 
                          }}
                        >
                          {getStatusIcon(event.status)}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Box>
                            <Typography variant="body2" component="span" fontWeight="medium">
                              {event.event_type.toUpperCase()}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                              {format(new Date(event.timestamp), 'MMM dd, yyyy HH:mm')}
                            </Typography>
                          </Box>
                        }
                        secondary={
                          <Box sx={{ mt: 0.5 }}>
                            <Typography variant="body2">
                              {event.description}
                            </Typography>
                            <Chip
                              size="small"
                              label={event.source}
                              variant="outlined"
                              sx={{ mt: 0.5 }}
                            />
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OutageDetailsModal;
